import { formatDate } from '@/utils/constants';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import MessageActions from '../MessageActions';
import { safelyParseDate } from '@/utils/date';
import Citation from './Citation';
import LinkRenderer from '@/components/LinkRenderer';
import UserMessageAttachments from './UserMessageAttachments';

const AiMessage = ({ message }) => {
	const theme = useTheme();
	const [expanded, setExpanded] = useState(false);
	const [renderedContent, setRenderedContent] = useState(message?.content ?? '');

	const timeoutRef = useRef(null);

	const assistant = message.role === 'assistant';

	const [isTyping, setIsTyping] = useState(false);
	const shouldAnimate = assistant && Boolean(message?.animate);

	useEffect(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		const fullContent = String(message?.content ?? '');

		if (!shouldAnimate || !fullContent) {
			setRenderedContent(fullContent);
			setIsTyping(false);
			return;
		}

		let cursor = 0;
		setRenderedContent('');
		setIsTyping(true);

		const tick = () => {
			const currentChar = fullContent[cursor] ?? '';

			const step =
				currentChar === '\n'
					? 1
					: /[.,!?]/.test(currentChar)
						? 1
						: cursor < 120
							? 2
							: cursor < 360
								? 3
								: 5;

			cursor = Math.min(fullContent.length, cursor + step);
			setRenderedContent(fullContent.slice(0, cursor));

			if (cursor >= fullContent.length) {
				setIsTyping(false);
				return;
			}

			const delay = currentChar === '\n' ? 24 : /[.,!?]/.test(currentChar) ? 42 : 16;
			timeoutRef.current = setTimeout(tick, delay);
		};

		timeoutRef.current = setTimeout(tick, 40);

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [message?.id, message?.messageId, message?.content, shouldAnimate]);

	const hasCitations = Array.isArray(message.citations) && message.citations.length > 0;

	const BOX_SX = useMemo(
		() => ({
			...(!assistant && {
				py: 0.5,
				px: 1,
				borderRadius: '8px',
				backgroundColor: theme.palette.action.selected,
				ml: 'auto',
				mr: 0.5,
			}),
			maxWidth: assistant ? '100%' : 'fit-content',
		}),
		[assistant, theme],
	);

	const messageSent = useMemo(() => {
		const timestamp = safelyParseDate(message.createdAt);
		if (timestamp) {
			const date = formatDate(timestamp);
			return `Sent${assistant ? '' : ' by you'} ${date}`;
		}
	}, [message.createdAt, assistant]);

	return (
		<Stack>
			<Box
				sx={{
					maxWidth: '100%',
					ml: assistant ? 0 : 'auto',
				}}
			>
				<Box sx={BOX_SX}>
					{!assistant && <UserMessageAttachments message={message} />}
					<Box
						sx={{
							'& p': {
								my: assistant ? 0.6 : 0.15,
								lineHeight: assistant ? 1.78 : 1.5,
							},
							'& ul, & ol': {
								pl: 3,
								my: assistant ? 0.8 : 0.35,
							},
							'& a': {
								color: theme.palette.folioPurple,
							},
						}}
					>
						<ReactMarkdown
							components={{ a: LinkRenderer, link: LinkRenderer }}
							remarkPlugins={[remarkGfm]}
						>
							{renderedContent}
						</ReactMarkdown>
						{isTyping && (
							<Typography
								component='span'
								aria-hidden='true'
								sx={{
									display: 'inline-flex',
									alignItems: 'center',
									color: theme.palette.folioPurple,
									fontWeight: 700,
									ml: 0.2,
									animation: 'folio-ai-cursor 1s steps(1) infinite',
									'@keyframes folio-ai-cursor': {
										'0%, 49%': {
											opacity: 1,
										},
										'50%, 100%': {
											opacity: 0.2,
										},
									},
								}}
							>
								▍
							</Typography>
						)}
					</Box>
					{messageSent && assistant && !isTyping && (
						<Typography variant='task_updated' component={'div'} sx={{ my: 1 }}>
							{messageSent}
						</Typography>
					)}
					{assistant && !isTyping && hasCitations && (
						<>
							<Stack spacing={1}>
								<Button
									onClick={() => setExpanded((prev) => !prev)}
									endIcon={
										expanded ? (
											<ExpandLessIcon
												sx={{
													fontSize: 18,
												}}
											/>
										) : (
											<ExpandMoreIcon
												sx={{
													fontSize: 18,
												}}
											/>
										)
									}
									variant='text'
									sx={{
										alignSelf: 'flex-start',
										minWidth: 0,
										p: 0,
										pt: 0.5,
										color: theme.palette.text.secondary,
										fontWeight: 600,
										fontSize: '13px',
										textTransform: 'none',
										'&:hover': {
											backgroundColor: 'transparent',
											color: theme.palette.text.primary,
										},
									}}
								>
									Sources ({message.citations.length})
								</Button>
								{expanded &&
									message.citations.map((citation, citationIndex) => (
										<Citation
											key={`${citation.sourceId ?? citation.title}-${citationIndex}`}
											{...citation}
										/>
									))}
							</Stack>
						</>
					)}

					{assistant && !isTyping && (
						<Box sx={{ mt: 1 }}>
							<MessageActions {...{ message }} />
						</Box>
					)}
				</Box>
			</Box>
			{messageSent && !assistant && (
				<Box sx={{ alignSelf: 'flex-end' }}>
					<Typography variant='task_updated'>{messageSent}</Typography>
				</Box>
			)}
		</Stack>
	);
};
export default AiMessage;
